<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

import {
  SOURCE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  cancelOrder,
  closeOrder,
  createOrder,
  deleteOrder,
  getOrder,
  importOrders,
  importTemplate,
  listOrders,
  releaseOrder,
  updateOrder,
  type ImportResult,
  type ItemInput,
  type ListRow,
  type OrderInput,
  type OutboundSource,
  type OutboundStatus,
} from '../api/outbound'
import { listCustomers, type Customer } from '../api/customers'
import { listSkus, type Sku } from '../api/skus'
import { listWarehouses, type Warehouse } from '../api/warehouses'

const router = useRouter()

const rows = ref<ListRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const warehouses = ref<Warehouse[]>([])
const customers = ref<Customer[]>([])
const skus = ref<Sku[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  warehouse: undefined as number | undefined,
  customer: undefined as number | undefined,
  status: '',
  source: '',
  page: 1,
  size: 20,
})

const formOpen = ref(false)
const editing = ref<ListRow | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  warehouse_id: undefined as number | undefined,
  customer_id: undefined as number | undefined,
  expect_ship_at: null as string | null,
  remark: '',
  lines: [] as ItemInput[],
})

/** 仓库/客户下拉与表格展示共用：id → 「编码 名称」 */
function label(id: number, list: { id: number; code: string; name: string }[]): string {
  const hit = list.find((item) => item.id === id)
  return hit ? `${hit.code} ${hit.name}`.trim() : `#${id}`
}

const formErrorText = computed(() => {
  if (!form.warehouse_id) return '请选择仓库'
  if (!form.customer_id) return '请选择客户'
  if (!form.lines.length) return '请至少添加一行商品'
  for (const [i, line] of form.lines.entries()) {
    if (!line.sku_id) return `第 ${i + 1} 行请选择商品`
    if (!(line.qty > 0)) return `第 ${i + 1} 行数量必须大于 0`
  }
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listOrders({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      warehouse: query.warehouse || undefined,
      customer: query.customer || undefined,
      status: query.status || undefined,
      source: query.source || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    warehouses.value = (await listWarehouses({ page: 1, size: 100 })).list
    customers.value = (await listCustomers({ page: 1, size: 200 })).list
    skus.value = (await listSkus({ page: 1, size: 200 })).list
    if (!form.warehouse_id && warehouses.value.length) form.warehouse_id = warehouses.value[0].id
  } catch (e) {
    // 参照数据拿不到下拉会空，但必须把原因露出来（如未选货主时后端回 422），否则页面看着像没数据
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    warehouse_id: warehouses.value[0]?.id,
    customer_id: customers.value[0]?.id,
    expect_ship_at: null,
    remark: '',
    lines: [] as ItemInput[],
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

async function openEdit(row: ListRow): Promise<void> {
  editing.value = row
  resetForm()
  formError.value = null
  try {
    const d = await getOrder(row.id)
    form.warehouse_id = d.order.warehouse_id
    form.customer_id = d.order.customer_id
    form.expect_ship_at = toLocal(d.order.expect_ship_at)
    form.remark = d.order.remark
    form.lines = d.items.map((item) => ({ sku_id: item.sku_id, qty: item.qty }))
  } catch (e) {
    formError.value = e
  }
  formOpen.value = true
}

/** 后端时间是 RFC3339（带时区偏移），取到秒并还原成本地墙钟时间给日期控件 */
function toLocal(iso: string | null): string | null {
  if (!iso) return null
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`
}

function addLine(): void {
  form.lines.push({ sku_id: 0, qty: 1 })
}

function removeLine(index: number): void {
  form.lines.splice(index, 1)
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input: OrderInput = {
    warehouse_id: form.warehouse_id ?? 0,
    customer_id: form.customer_id ?? 0,
    expect_ship_at: form.expect_ship_at,
    remark: form.remark.trim(),
    items: form.lines.map((line) => ({ sku_id: line.sku_id, qty: Number(line.qty) })),
  }
  try {
    if (editing.value) await updateOrder(editing.value.id, input)
    else await createOrder(input)
    formOpen.value = false
    await load(editing.value ? '出库单已更新' : '出库单已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: ListRow): Promise<void> {
  notice.value = ''
  try {
    await deleteOrder(row.id)
    await load(`已删除出库单「${row.doc_no}」`)
  } catch (e) {
    error.value = e
  }
}

/** 状态流转统一走这里：成功刷新并提示，失败把错误挂到页头告警区 */
async function act(
  row: ListRow,
  action: 'release' | 'cancel' | 'close',
  text: string,
): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    if (action === 'release') await releaseOrder(row.id)
    else if (action === 'cancel') await cancelOrder(row.id)
    else await closeOrder(row.id)
    await load(text)
  } catch (e) {
    error.value = e
  }
}

function open(row: ListRow): void {
  void router.push({ name: 'outbound-detail', params: { id: row.id } })
}

const canEdit = (status: OutboundStatus) => status === 'draft'
const canCancel = (status: OutboundStatus) => status === 'draft' || status === 'released'
const canClose = (status: OutboundStatus) => status === 'picked' || status === 'shipped'

// ---- CSV 导入 ----

const importOpen = ref(false)
const importing = ref(false)
const importError = ref<unknown>(null)
const importResult = ref<ImportResult | null>(null)
const csvText = ref('')

/** Blob 存成文件下载：模板与（T9 的）导出共用同一套存盘手法 */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

async function downloadTemplate(): Promise<void> {
  importError.value = null
  try {
    saveBlob(await importTemplate(), '出库导入模板.csv')
  } catch (e) {
    importError.value = e
  }
}

async function onFile(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file) return
  csvText.value = await file.text()
}

async function doImport(): Promise<void> {
  if (!csvText.value.trim()) return
  importing.value = true
  importError.value = null
  importResult.value = null
  try {
    const res = await importOrders(csvText.value)
    importResult.value = res
    if (res.created > 0) await load(`导入成功：${res.created} 张出库单`)
  } catch (e) {
    importError.value = e
    // 全部行失败时后端回 400，但逐行错误仍在 Data 里，照常显示给用户
    importResult.value = partialErrors(e)
  } finally {
    importing.value = false
  }
}

/** 从 400 的信封里把逐行明细捞出来；别的错误返回 null。 */
function partialErrors(e: unknown): ImportResult | null {
  const data = (e as { data?: unknown }).data
  const result = data as ImportResult | undefined
  if (!result || typeof result.created !== 'number' || !Array.isArray(result.errors)) return null
  return result
}
</script>

<template>
  <div class="yc-outb">
    <header class="yc-outb__bar">
      <el-input
        v-model="query.keyword"
        class="yc-outb__keyword"
        placeholder="出库单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.warehouse" class="yc-outb__ref" placeholder="全部仓库" clearable @change="search">
        <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
      </el-select>
      <el-select v-model="query.customer" class="yc-outb__ref" placeholder="全部客户" clearable @change="search">
        <el-option v-for="c in customers" :key="c.id" :label="label(c.id, customers)" :value="c.id" />
      </el-select>
      <el-select v-model="query.status" class="yc-outb__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(text, value) in STATUS_LABELS" :key="value" :label="text" :value="value" />
      </el-select>
      <el-select v-model="query.source" class="yc-outb__status" placeholder="全部来源" clearable @change="search">
        <el-option v-for="(text, value) in SOURCE_LABELS" :key="value" :label="text" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'wms:outbound:create'" type="primary" @click="openCreate">新建出库单</el-button>
      <el-button v-perm="'wms:outbound:import'" @click="importOpen = true">批量导入</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-outb__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-outb__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-outb__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="出库单号" width="170" />
        <el-table-column label="仓库" min-width="130">
          <template #default="{ row }">{{ row.warehouse_name || `#${row.warehouse_id}` }}</template>
        </el-table-column>
        <el-table-column label="客户" min-width="150">
          <template #default="{ row }">{{ row.customer_name || `#${row.customer_id}` }}</template>
        </el-table-column>
        <el-table-column label="状态" width="96">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as OutboundStatus]" size="small">
              {{ STATUS_LABELS[row.status as OutboundStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="来源" width="96">
          <template #default="{ row }">{{ SOURCE_LABELS[row.source as OutboundSource] ?? row.source }}</template>
        </el-table-column>
        <el-table-column label="应发 / 已拣" width="150">
          <template #default="{ row }">
            <el-progress
              :percentage="row.total_qty ? Math.min(100, Math.round((row.picked_qty / row.total_qty) * 100)) : 0"
              :stroke-width="8"
              :status="row.picked_qty >= row.total_qty ? 'success' : undefined"
            />
            <span class="yc-outb__qty">{{ row.picked_qty }} / {{ row.total_qty }}</span>
          </template>
        </el-table-column>
        <el-table-column label="期望发货" min-width="150">
          <template #default="{ row }">
            {{ row.expect_ship_at ? formatDateTime(row.expect_ship_at) : '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="created_by" label="创建人" width="110" />
        <el-table-column label="创建时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="300" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="open(row)">详情</el-button>
            <el-button
              v-perm="'wms:outbound:update'"
              v-if="canEdit(row.status)"
              link
              type="primary"
              @click="openEdit(row)"
            >
              编辑
            </el-button>
            <el-button
              v-perm="'wms:outbound:release'"
              v-if="canEdit(row.status)"
              link
              type="primary"
              @click="act(row, 'release', `出库单「${row.doc_no}」已释放`)"
            >
              释放
            </el-button>
            <el-button
              v-perm="'wms:outbound:close'"
              v-if="canClose(row.status)"
              link
              type="warning"
              @click="act(row, 'close', `出库单「${row.doc_no}」已关闭`)"
            >
              关闭
            </el-button>
            <el-popconfirm
              v-if="canCancel(row.status)"
              title="确认取消该出库单？"
              width="200"
              @confirm="act(row, 'cancel', `出库单「${row.doc_no}」已取消`)"
            >
              <template #reference>
                <el-button v-perm="'wms:outbound:cancel'" link type="danger">取消</el-button>
              </template>
            </el-popconfirm>
            <el-popconfirm v-if="canEdit(row.status)" title="确认删除该出库单？" width="200" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'wms:outbound:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState v-else-if="!loading" title="还没有出库单" description="手工建单或批量导入，释放后就能波次拣货、发货扣库存。">
        <el-button v-perm="'wms:outbound:create'" type="primary" @click="openCreate">新建出库单</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-outb__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑出库单 · ${editing.doc_no}` : '新建出库单'"
      width="720px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-outb__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-outb__formrow">
          <el-form-item label="所属仓库">
            <el-select v-model="form.warehouse_id" class="yc-outb__ref">
              <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="客户">
            <el-select v-model="form.customer_id" class="yc-outb__ref">
              <el-option v-for="c in customers" :key="c.id" :label="label(c.id, customers)" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="期望发货时间">
            <el-date-picker
              v-model="form.expect_ship_at"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
              placeholder="可留空"
            />
          </el-form-item>
        </div>

        <el-form-item label="备注">
          <el-input v-model="form.remark" maxlength="255" placeholder="可留空" />
        </el-form-item>

        <el-form-item label="出库明细">
          <div class="yc-outb__lines">
            <div v-for="(line, index) in form.lines" :key="index" class="yc-outb__line">
              <el-select v-model="line.sku_id" class="yc-outb__sku" filterable placeholder="选择商品">
                <el-option
                  v-for="s in skus"
                  :key="s.id"
                  :label="`${s.code} ${s.name}`"
                  :value="s.id"
                />
              </el-select>
              <el-input-number v-model="line.qty" :min="1" :max="999999" controls-position="right" />
              <el-button link type="danger" @click="removeLine(index)">删除</el-button>
            </div>
            <div class="yc-outb__line">
              <el-button @click="addLine">添加一行</el-button>
              <span class="yc-outb__hint">同一商品重复添加会自动合并数量</span>
            </div>
          </div>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-outb__hint yc-outb__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="importOpen" title="批量导入出库单" width="720px" :close-on-click-modal="false">
      <div v-if="importError" class="yc-outb__error">
        <TraceIdAlert :error="importError" />
      </div>

      <div class="yc-outb__importbar">
        <el-button @click="downloadTemplate">下载模板</el-button>
        <span class="yc-outb__hint">列为：客户编码、SKU编码、数量、期望发货日期；同客户同日期合并成一张单。</span>
      </div>

      <el-input
        v-model="csvText"
        type="textarea"
        :rows="8"
        placeholder="选择 CSV 文件，或把内容直接粘贴到这里"
      />
      <input class="yc-outb__file" type="file" accept=".csv,text/csv" @change="onFile" />

      <el-button
        type="primary"
        :loading="importing"
        :disabled="!csvText.trim()"
        @click="doImport"
      >
        开始导入
      </el-button>

      <div v-if="importResult" class="yc-outb__importresult">
        <el-alert
          v-if="importResult.created > 0"
          :title="`导入成功：${importResult.created} 张出库单`"
          type="success"
          :closable="false"
          show-icon
        />
        <el-table
          v-if="importResult.errors.length"
          :data="importResult.errors"
          size="small"
          max-height="240"
          class="yc-outb__errtable"
        >
          <el-table-column prop="line" label="行号" width="80" />
          <el-table-column prop="msg" label="错误" min-width="320" />
        </el-table>
        <p v-if="importResult.created === 0 && !importResult.errors.length" class="yc-outb__hint">
          没有导入任何数据。
        </p>
      </div>

      <template #footer>
        <el-button @click="importOpen = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-outb {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 170px;
  }

  &__ref {
    width: 190px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__qty {
    display: block;
    margin-top: var(--yc-space-1);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__formrow {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yc-space-3);

    .el-form-item {
      margin-bottom: var(--yc-space-3);
    }
  }

  &__lines {
    width: 100%;
  }

  &__line {
    display: flex;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-2);
  }

  &__sku {
    flex: 1;
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.6;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__importbar {
    display: flex;
    align-items: center;
    gap: var(--yc-space-3);
    margin-bottom: var(--yc-space-3);
  }

  &__file {
    display: block;
    margin: var(--yc-space-3) 0;
    font-size: var(--yc-font-sm);
  }

  &__importresult {
    margin-top: var(--yc-space-3);
  }

  &__errtable {
    margin-top: var(--yc-space-2);
  }
}
</style>
